import React from 'react';

const suitSymbols = { spades: "♠", hearts: "♥", diamonds: "♦", clubs: "♣" };
const suitColors = { spades: "#333", clubs: "#333", hearts: "#d22", diamonds: "#d22" };

/**
 * PlayerSeat Component
 * Renders minimal seat info: avatar, name, and essential status badges
 */
export default function PlayerSeat({ 
  seatState, 
  position, 
  isLocalPlayer,
  dealerSuit,
  creditValue = 1.00,
  suitVote = null, // Player's suit vote
  phase = 'waiting', // Current game phase
  hasFreeHand = false // Whether player has a free hand
}) {
  const { player, isDealer, isActive, statusBadges, visualState, credits, isWinner } = seatState;
  
  const suitSymbols = { spades: "♠", hearts: "♥", diamonds: "♦", clubs: "♣" };
  
  return (
    <div
      style={{
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        zIndex: visualState.isHighlighted ? 10 : 3,
        opacity: visualState.isDimmed ? 0.5 : 1,
        transition: 'all 0.3s ease',
        filter: visualState.isActive ? 'drop-shadow(0 0 20px rgba(59, 130, 246, 0.8))' : 'none'
      }}
    >
      {/* Active turn glow - reduced proportionally */}
      {visualState.isActive && (
        <div
          style={{
            position: 'absolute',
            width: '76px', // Reduced proportionally (80px * 0.95)
            height: '76px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, transparent 70%)',
            animation: 'pulse 1s ease-in-out infinite',
            zIndex: -1
          }}
        />
      )}
      
      {/* Winner box - displayed above avatar */}
      {isWinner && (
        <div
          style={{
            position: 'absolute',
            top: '-50px',
            background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)', // Brighter blue gradient
            color: '#ffffff',
            padding: '6px 12px',
            borderRadius: '8px',
            fontWeight: '700',
            fontSize: '0.8rem',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.6), 0 0 20px rgba(59, 130, 246, 0.4)', // Bright glow
            border: '2px solid #ffffff',
            whiteSpace: 'nowrap',
            zIndex: 7,
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
            animation: 'pulse 1.5s ease-in-out infinite'
          }}
        >
          🏆 Winner!
        </div>
      )}
      
      {/* Dealer button */}
      {visualState.showDealerButton && (
        <div
          style={{
            position: 'absolute',
            top: isWinner ? '-20px' : '-20px', // Adjust if winner box is shown
            background: '#fbbf24',
            color: '#78350f',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '700',
            fontSize: '0.75rem',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
            border: '2px solid #d97706',
            zIndex: 6
          }}
        >
          D
        </div>
      )}
      
      {/* Avatar - reduced by 5% (56px * 0.95 = 53.2px, rounded to 53px) */}
      <div
        style={{
          width: '53px',
          height: '53px',
          borderRadius: '50%',
          border: `3px solid ${isLocalPlayer ? '#3b82f6' : player.isBot ? '#10b981' : '#64748b'}`,
          background: '#fff',
          padding: '2px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
          marginBottom: '6px',
          position: 'relative'
        }}
      >
        <img
          src={player.avatarUrl || '/avatars/avatar1.png'}
          alt={player.name}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            objectFit: 'cover'
          }}
        />
        
        {/* Status indicator dot */}
        {statusBadges.includes('ready') && (
          <div
            style={{
              position: 'absolute',
              bottom: '2px',
              right: '2px',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: '#10b981',
              border: '2px solid white',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
            }}
          />
        )}
      </div>
      
      {/* Name */}
      <div
        style={{
          background: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '0.75rem',
          fontWeight: '600',
          marginBottom: '4px',
          whiteSpace: 'nowrap',
          maxWidth: '100px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)'
        }}
      >
        {player.name}
        {player.isBot && ' 🤖'}
      </div>
      
      {/* Status badges - exclude winner badge if winner box is shown above */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          alignItems: 'center'
        }}
      >
        {statusBadges.filter(badge => badge !== 'winner' || !isWinner).map(badge => (
          <StatusBadge key={badge} badge={badge} />
        ))}
      </div>
      
      {/* Suit selection display (during suitvote phase) */}
      {phase === 'suitvote' && suitVote && (
        <div
          style={{
            marginTop: '4px',
            background: '#f0f9ff',
            color: '#1e40af',
            padding: '3px 8px',
            borderRadius: '4px',
            fontSize: '0.65rem',
            fontWeight: '600',
            border: '1px solid #3b82f6',
            whiteSpace: 'nowrap'
          }}
        >
          Suit selection: {suitSymbols[suitVote] || suitVote}
        </div>
      )}
      
      {/* Free Hand indicator - displayed under avatar */}
      {hasFreeHand && (
        <div
          style={{
            marginTop: '4px',
            background: '#d1fae5',
            color: '#059669',
            padding: '4px 10px',
            borderRadius: '6px',
            fontSize: '0.7rem',
            fontWeight: '700',
            border: '2px solid #10b981',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)'
          }}
        >
          Free Hand
        </div>
      )}
      
      {/* Credits (only for local player) */}
      {isLocalPlayer && (
        <div
          style={{
            marginTop: '4px',
            background: '#3b82f6',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.7rem',
            fontWeight: '600'
          }}
        >
          ${((credits || 0) * (creditValue || 1.00)).toFixed(2)}
        </div>
      )}
    </div>
  );
}

/**
 * StatusBadge Component
 * Renders a small status indicator badge
 */
function StatusBadge({ badge }) {
  const badgeConfig = {
    ready: { label: 'Ready', color: '#10b981', bg: '#d1fae5', borderColor: null },
    voted: { label: 'Voted', color: '#3b82f6', bg: '#dbeafe', borderColor: null },
    thinking: { label: 'Thinking...', color: '#f59e0b', bg: '#fef3c7', borderColor: null },
    knocked: { label: 'Knocked', color: '#10b981', bg: '#d1fae5', borderColor: null },
    discarded: { label: 'Discarded', color: '#ef4444', bg: '#fee2e2', borderColor: null },
    winner: { label: 'Winner!', color: '#f59e0b', bg: '#bfdbfe', borderColor: '#f59e0b' }, // Light blue background, orange text and border
    bot: { label: 'Bot', color: '#64748b', bg: '#e2e8f0', borderColor: null },
    dealer: { label: 'Dealer', color: '#fbbf24', bg: '#fef3c7', borderColor: null }
  };
  
  const config = badgeConfig[badge] || { label: badge, color: '#64748b', bg: '#e2e8f0', borderColor: null };
  
  // For winner badge, increase size by 5%
  const isWinner = badge === 'winner';
  const fontSize = isWinner ? '0.6825rem' : '0.65rem'; // 5% increase: 0.65 * 1.05 = 0.6825
  const padding = isWinner ? '2.1px 6.3px' : '2px 6px'; // 5% increase
  
  return (
    <div
      style={{
        background: config.bg,
        color: config.color,
        padding: padding,
        borderRadius: '4px',
        fontSize: fontSize,
        fontWeight: '600',
        whiteSpace: 'nowrap',
        border: config.borderColor ? `2px solid ${config.borderColor}` : 'none' // Orange border for winner
      }}
    >
      {config.label}
    </div>
  );
}

// Add pulse animation
const style = document.createElement('style');
style.textContent = `
  @keyframes pulse {
    0%, 100% { opacity: 0.6; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.1); }
  }
`;
if (!document.head.querySelector('#pulse-animation')) {
  style.id = 'pulse-animation';
  document.head.appendChild(style);
}

